import React, { useEffect, useRef } from 'react';
function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
export function ParaisoStandard() {
  const headerRef = useScrollReveal(0);
  const item1Ref = useScrollReveal(100);
  const item2Ref = useScrollReveal(200);
  const item3Ref = useScrollReveal(300);
  const item4Ref = useScrollReveal(400);
  const closingRef = useScrollReveal(500);
  return <section className="w-full py-20 sm:py-28 relative overflow-hidden" style={{
    backgroundColor: '#0a0a0a'
  }} aria-label="The By Guerra Standard" data-id="element-378">
      {/* Gold accent line top */}
      <div className="gold-divider-full absolute top-0 left-0 right-0" data-id="element-379" />

      {/* Background texture */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.04) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-380" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-381">
        <div ref={headerRef} className="text-center mb-16" data-id="element-382">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-383">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-384" />
            <span className="section-label" data-id="element-385">The Solution</span>
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-386" />
          </div>

          {/* Scissors icon */}
          <div className="flex justify-center mb-6" data-id="element-387">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(201,168,76,0.3)'
          }} data-id="element-388">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-389">
                <circle cx="6" cy="6" r="3" data-id="element-390" />
                <circle cx="6" cy="18" r="3" data-id="element-391" />
                <line x1="20" y1="4" x2="8.12" y2="15.88" data-id="element-392" />
                <line x1="14.47" y1="14.48" x2="20" y2="20" data-id="element-393" />
                <line x1="8.12" y1="8.12" x2="12" y2="12" data-id="element-394" />
              </svg>
            </div>
          </div>

          <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          fontWeight: 900,
          color: '#F5F5F5',
          lineHeight: 1.2,
          marginBottom: '1rem'
        }} data-id="element-395">
            Introducing:{' '}
            <span style={{
            color: '#C9A84C',
            fontStyle: 'italic'
          }} data-id="element-396">
              The By Guerra Standard
            </span>
          </h2>
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.05rem',
          color: '#888',
          lineHeight: 1.7,
          maxWidth: '600px',
          margin: '0 auto'
        }} data-id="element-397">
            A great barbershop doesn't make you choose between skill, speed, and
            respect. It gives you all three — every single visit.
          </p>
          <p className="mt-3" style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1rem',
          color: '#aaa',
          lineHeight: 1.7
        }} data-id="element-398">
            That's what we built at By Guerra Barbershop, right here in Hialeah
            at Paraíso Plaza 2.
          </p>
        </div>

        {/* Standard items */}
        <div className="space-y-4" data-id="element-399">
          <div ref={item1Ref} data-id="element-400">
            <StandardItem text="Walk in anytime — no appointment, no scheduling apps, no waiting days" data-id="element-401" />
          </div>
          <div ref={item2Ref} data-id="element-402">
            <StandardItem text="Get the cut you asked for — not a close approximation" data-id="element-403" />
          </div>
          <div ref={item3Ref} data-id="element-404">
            <StandardItem text="Be treated like a regular — from your very first visit" data-id="element-405" />
          </div>
          <div ref={item4Ref} data-id="element-406">
            <StandardItem text="Leave looking sharp — every single time, no exceptions" data-id="element-407" />
          </div>
        </div>

        <div ref={closingRef} className="mt-12 text-center" data-id="element-408">
          <p style={{
          fontFamily: 'var(--font-callout)',
          fontSize: 'clamp(1rem, 2vw, 1.2rem)',
          fontWeight: 600,
          color: '#888',
          letterSpacing: '0.05em',
          textTransform: 'uppercase'
        }} data-id="element-409">
            This isn't a gimmick.
          </p>
          <p style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
          fontStyle: 'italic',
          color: '#C9A84C',
          marginTop: '0.5rem'
        }} data-id="element-410">
            It's just what a real barbershop is supposed to be.
          </p>

          <div className="mt-10" data-id="element-411">
            <a href="#location" className="btn-gold inline-flex items-center gap-3 px-8 py-4 text-base rounded-sm" data-id="element-412">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-413">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-414" />
                <circle cx="12" cy="10" r="3" data-id="element-415" />
              </svg>
              Come See the Difference
            </a>
          </div>
        </div>
      </div>

      <div className="gold-divider-full absolute bottom-0 left-0 right-0" data-id="element-416" />
    </section>;
}
function StandardItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-5 p-5 sm:p-6 rounded-sm" style={{
    backgroundColor: '#111111',
    border: '1px solid rgba(201,168,76,0.15)'
  }} data-id="element-417">
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
      backgroundColor: 'rgba(201,168,76,0.15)',
      border: '1px solid rgba(201,168,76,0.4)'
    }} data-id="element-418">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-419">
          <polyline points="20 6 9 17 4 12" data-id="element-420" />
        </svg>
      </div>
      <p style={{
      fontFamily: 'var(--font-body)',
      fontSize: '1.05rem',
      color: '#F5F5F5',
      lineHeight: 1.6
    }} data-id="element-421">
        {text}
      </p>
    </div>;
}